import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
// import { CartPage } from "./pages/CartPage";
import {
  LayoutDashboard,
  Users,
  ShoppingCart,
  FileText,
  UserCheck,
  DollarSign,
  Menu,
  LogOut,
  TrendingUp,
  Moon,
  Sun,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Package,
  User,
  Settings,
  Megaphone,
  Target,
  FolderOpen,
  Utensils,
  Tent,
  Mail,
  ClipboardList,
  MapPin,
} from "lucide-react";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/profile", label: "Profilo Azienda", icon: User, internalOnly: false },
  { path: "/promos", label: "Promo Canvass", icon: Megaphone },
  { path: "/products", label: "Prodotti", icon: Package },
  { path: "/materiale-digitale", label: "Materiale Digitale", icon: FolderOpen },
  { path: "/leads", label: "Lead", icon: Target, internalOnly: true },
  { 
    label: "Clienti", 
    icon: Users, 
    internalOnly: true,
    isSubmenu: true,
    children: [
      { path: "/clients", label: "Elenco Clienti", icon: Users },
      { path: "/clients/map", label: "Mappa Clienti", icon: MapPin },
    ]
  },
  { path: "/orders", label: "Ordini", icon: ShoppingCart },
  { path: "/quotes", label: "Preventivi", icon: FileText },
  { path: "/agents", label: "Agenti", icon: UserCheck, adminOnly: true },
  { path: "/commissions", label: "Provvigioni", icon: DollarSign, internalOnly: true },
  { 
    label: "Altri Form", 
    icon: ClipboardList, 
    adminOnly: true, 
    sendmeVisible: true,
    isSubmenu: true,
    children: [
      { path: "/sendme-admin", label: "SendMe Pasti", icon: Utensils, sendmeVisible: true },
      { path: "/sendme-camp-admin", label: "SendMe Conference", icon: Tent, sendmeVisible: true, sendmeConferenceVisible: true },
    ]
  },
  { path: "/email-templates", label: "Template Email", icon: Mail, adminOnly: true },
  { path: "/settings", label: "Impostazioni", icon: Settings, adminOnly: true },
];

export default function DashboardLayout() {
  const { user, logout, isAdmin } = useAuth();
  const { totalItems, setIsOpen: setCartOpen } = useCart();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedSubmenu, setExpandedSubmenu] = useState(null);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle("dark");
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isInternal = user?.role !== "client";
  const isSendmeAdmin = user?.role === "sendme_admin";
  const isSendmeConferenceAdmin = user?.role === "sendme_conference_admin";

  const shouldShowItem = (item) => {
    // SendMe Conference Admin users can ONLY see items with sendmeConferenceVisible
    if (isSendmeConferenceAdmin) {
      return item.sendmeConferenceVisible === true;
    }
    // SendMe Admin users can see both SendMe sections
    if (isSendmeAdmin) {
      return item.sendmeVisible === true;
    }
    if (item.adminOnly && !isAdmin) return false;
    if (!isInternal && item.internalOnly === true) return false;
    return true;
  };

  const filteredNavItems = navItems.filter((item) => {
    if (item.isSubmenu) {
      // Filter children and check if any are visible
      const visibleChildren = item.children.filter(shouldShowItem);
      return visibleChildren.length > 0 && shouldShowItem(item);
    }
    return shouldShowItem(item);
  });

  const toggleSubmenu = (label) => {
    setExpandedSubmenu(expandedSubmenu === label ? null : label);
  };

  const NavContent = ({ mobile = false }) => (
    <nav className="space-y-1">
      {filteredNavItems.map((item) => {
        if (item.isSubmenu) {
          const visibleChildren = item.children.filter(shouldShowItem);
          const isExpanded = expandedSubmenu === item.label;
          
          return (
            <div key={item.label}>
              <button
                onClick={() => toggleSubmenu(item.label)}
                className={`sidebar-item w-full ${
                  sidebarCollapsed && !mobile ? "justify-center px-2" : "justify-between"
                }`}
              >
                <div className="flex items-center gap-3">
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {(!sidebarCollapsed || mobile) && (
                    <span className="truncate">{item.label}</span>
                  )}
                </div>
                {(!sidebarCollapsed || mobile) && (
                  <ChevronDown 
                    className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} 
                  />
                )}
              </button>
              {isExpanded && (!sidebarCollapsed || mobile) && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-muted pl-2">
                  {visibleChildren.map((child) => (
                    <NavLink
                      key={child.path}
                      to={child.path}
                      onClick={() => mobile && setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `sidebar-item text-sm ${isActive ? "active" : ""}`
                      }
                      data-testid={`nav-${child.path.slice(1)}`}
                    >
                      <child.icon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{child.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        }
        
        return (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => mobile && setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `sidebar-item ${isActive ? "active" : ""} ${
                sidebarCollapsed && !mobile ? "justify-center px-2" : ""
              }`
            }
            data-testid={`nav-${item.path.slice(1)}`}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {(!sidebarCollapsed || mobile) && (
              <span className="truncate">{item.label}</span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col border-r border-border/50 bg-card transition-all duration-300 ${
          sidebarCollapsed ? "w-16" : "w-64"
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border/50">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-primary font-bold">Lacinio.net</span>
            </div>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
            data-testid="sidebar-toggle"
          >
            <ChevronLeft
              className={`w-5 h-5 transition-transform ${
                sidebarCollapsed ? "rotate-180" : ""
              }`}
            />
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 p-3 overflow-y-auto">
          <NavContent />
        </div>

        {/* User section */}
        <div className="p-3 border-t border-border/50">
          <div
            className={`flex items-center gap-3 ${
              sidebarCollapsed ? "justify-center" : ""
            }`}
          >
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-accent text-white text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">
                  {user?.role}
                </p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="h-16 flex items-center justify-between px-4 lg:px-6 border-b border-border/50 bg-card">
          {/* Mobile menu */}
          <div className="lg:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <div className="h-16 flex items-center px-4 border-b border-border/50">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-xl font-primary font-bold">
                      NexSales
                    </span>
                  </div>
                </div>
                <div className="p-3">
                  <NavContent mobile />
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-primary font-bold">NexSales</span>
          </div>

          {/* Desktop spacer */}
          <div className="hidden lg:block" />

          {/* Right actions */}
          <div className="flex items-center gap-2">
            {/* Cart Trigger */}
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => navigate("/cart")}
              data-testid="cart-btn"
            >
              <ShoppingCart className="w-5 h-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[10px] text-white">
                  {totalItems}
                </span>
              )}
            </Button>
            {/* Remove CartSheet since we use page */}
            {/* <CartSheet /> */}

            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDarkMode}
              data-testid="theme-toggle"
            >
              {darkMode ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="gap-2"
                  data-testid="user-menu-btn"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-accent text-white text-sm">
                      {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user?.name}</span>
                    <span className="text-xs text-muted-foreground font-normal">
                      {user?.email}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-destructive cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
